import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authenticatedFetch } from '../utils/api';
import styles from '../styles/Assessment.module.css';
import { useDocumentHead } from '../hooks/useDocumentHead';
import { useAuth } from '../context/AuthContext';

const Assessment = () => {
    useDocumentHead(
        'User Assessment | TopSpeaks',
        'Configure your personalized speaking practice environment. Set your goals, target audience, and skill focus areas for tailored AI feedback and coaching recommendations.',
        'index, follow'
    );

    const navigate = useNavigate();
    const [showQuestions, setShowQuestions] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [step, setStep] = useState(1);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [existingAssessment, setExistingAssessment] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const { user, setAssessmentCompleted } = useAuth();
    const [answers, setAnswers] = useState({
        skillToImprove: [],
        presentationEnvironment: '',
        confidenceWithoutPractice: '',
        confidenceWithPractice: '',
        gender: ''
    });

    useEffect(() => {
        const fetchAssessment = async () => {
            if (showQuestions || isEditing) {
                return;
            }

            setLoading(true);
            try {
                const response = await authenticatedFetch('/api/assessment/get');
                if (response) {
                    setExistingAssessment(response);
                    setAnswers({
                        skillToImprove: Array.isArray(response.skill_to_improve)
                            ? response.skill_to_improve
                            : [response.skill_to_improve],
                        presentationEnvironment: response.presentation_environment,
                        confidenceWithoutPractice: response.confidence_without_practice,
                        confidenceWithPractice: response.confidence_with_practice,
                        gender: response.gender || ''
                    });
                }
            } catch (error) {
                // Match the exact error message from backend
                if (error.message === "Assessment not found") {
                    setShowQuestions(true);
                } else {
                    setError('Failed to fetch assessment data');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchAssessment();
    }, [user, showQuestions, isEditing]);

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await authenticatedFetch('/api/assessment/submit', {
                method: 'POST',
                body: answers
            });

            // Update assessment completed status in AuthContext
            setAssessmentCompleted(true);

            // Update user state in localStorage with new assessment status
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                const userData = JSON.parse(storedUser);
                userData.assessment_completed = true;
                localStorage.setItem('user', JSON.stringify(userData));
            }

            // Redirect to dashboard
            navigate('/dashboard');
        } catch (error) {
            setError(error.message);
            console.error('Error submitting assessment:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className={styles.shellContainer}>
                <div className={styles.contentWrapper}>
                    <div className={styles.card}>
                        <div className={styles.loadingSpinner}>Loading...</div>
                    </div>
                </div>
            </div>
        );
    }

    if (existingAssessment && !isEditing) {
        return (
            <div className={styles.shellContainer}>
                <div className={styles.contentWrapper}>
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h1>Your Assessment Profile</h1>
                            <p>Review and update your environment and practice preferences</p>
                        </div>
                        {error && <div className={styles.errorAlert}>{error}</div>}
                        <div className={styles.summaryContainer}>
                            <div className={styles.summaryCard}>
                                <h3>Presentation Type</h3>
                                <p>{existingAssessment.presentation_environment}</p>
                            </div>
                            <div className={styles.summaryCard}>
                                <h3>Gender</h3>
                                <p>{existingAssessment.gender}</p>
                            </div>
                            <div className={styles.summaryCard}>
                                <h3>Focus Areas</h3>
                                <p>{Array.isArray(existingAssessment.skill_to_improve)
                                    ? existingAssessment.skill_to_improve.join(', ')
                                    : existingAssessment.skill_to_improve}</p>
                            </div>
                            <div className={styles.summaryCard}>
                                <h3>Confidence Without Practice</h3>
                                <p>{existingAssessment.confidence_without_practice}</p>
                            </div>
                            <div className={styles.summaryCard}>
                                <h3>Confidence With Practice</h3>
                                <p>{existingAssessment.confidence_with_practice}</p>
                            </div>
                        </div>
                        <div className={styles.editContainer}>
                            <p className={styles.lastUpdated}>
                                Last updated: {new Date(existingAssessment.updated_at).toLocaleDateString()}
                            </p>
                            <button
                                className={styles.primaryButton}
                                onClick={() => setIsEditing(true)}
                            >
                                Edit Assessment
                            </button>

                            {/* go to practice button here. */}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!showQuestions) {
        return (
            <div className={styles.shellContainer}>
                <div className={styles.contentWrapper}>
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h1>Assessment Setup</h1>
                            <p>
                                Configure your practice environment for optimal presentation feedback
                            </p>
                        </div>
                        <div className={styles.cardContent}>
                            <p>
                                This assessment will help us customize your practice experience
                                by understanding your presentation style, confidence levels, and goals.
                                Your responses will be used to provide targeted feedback during your sessions.
                            </p>
                            <button
                                className={styles.primaryButton}
                                onClick={() => setShowQuestions(true)}
                            >
                                Begin Assessment
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const isStepValid = () => {
        switch (step) {
            case 1:
                return !!answers.gender;
            case 2:
                return !!answers.presentationEnvironment;
            case 3:
                return !!answers.confidenceWithoutPractice;
            case 4:
                return !!answers.confidenceWithPractice;
            case 5:
                return answers.skillToImprove.length > 0;
            default:
                return false;
        }
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className={styles.stepSection}>
                        <h2>Gender</h2>
                        <p className={styles.questionDescription}>What is your gender?</p>
                        <div className={styles.optionsContainer}>
                            {['Male', 'Female', 'Prefer not to say'].map(gender => (
                                <button
                                    key={gender}
                                    className={`${styles.optionCard} ${answers.gender === gender ? styles.selected : ''}`}
                                    onClick={() => setAnswers({ ...answers, gender: gender })}
                                >
                                    {gender}
                                </button>
                            ))}
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className={styles.stepSection}>
                        <h2>Presentation Environment</h2>
                        <p className={styles.questionDescription}>What type of presentations do you primarily give?</p>
                        <div className={styles.optionsContainer}>
                            {[
                                'Corporate Presentations',
                                'Social Media/YouTube Content',
                                'Storytelling/Entertainment',
                                'Academic/Technical',
                                'Motivational Speaking',
                                'University/Classroom',
                                'TV/Broadcasting',
                                'Sales/Client Presentations'
                            ].map(env => (
                                <button
                                    key={env}
                                    className={`${styles.optionCard} ${answers.presentationEnvironment === env ? styles.selected : ''}`}
                                    onClick={() => setAnswers({ ...answers, presentationEnvironment: env })}
                                >
                                    {env}
                                </button>
                            ))}
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className={styles.stepSection}>
                        <h2>Confidence Without Practice</h2>
                        <p className={styles.questionDescription}>How do you typically feel when presenting without preparation?</p>
                        <div className={styles.optionsContainer}>
                            {[
                                'Very Anxious',
                                'Somewhat Nervous',
                                'Neutral',
                                'Relatively Comfortable',
                                'Completely at Ease'
                            ].map(level => (
                                <button
                                    key={level}
                                    className={`${styles.optionCard} ${answers.confidenceWithoutPractice === level ? styles.selected : ''}`}
                                    onClick={() => setAnswers({ ...answers, confidenceWithoutPractice: level })}
                                >
                                    {level}
                                </button>
                            ))}
                        </div>
                    </div>
                );
            case 4:
                return (
                    <div className={styles.stepSection}>
                        <h2>Confidence With Practice</h2>
                        <p className={styles.questionDescription}>How confident do you feel after practicing your presentation?</p>
                        <div className={styles.optionsContainer}>
                            {[
                                'Still Very Anxious',
                                'Slightly More Confident',
                                'Moderately Confident',
                                'Significantly More Confident',
                                'Fully Confident'
                            ].map(level => (
                                <button
                                    key={level}
                                    className={`${styles.optionCard} ${answers.confidenceWithPractice === level ? styles.selected : ''}`}
                                    onClick={() => setAnswers({ ...answers, confidenceWithPractice: level })}
                                >
                                    {level}
                                </button>
                            ))}
                        </div>
                    </div>
                );
            case 5:
                return (
                    <div className={styles.stepSection}>
                        <h2>Primary Focus Areas</h2>
                        <p className={styles.questionDescription}>Which aspects of presenting would you like to improve? (Select multiple)</p>
                        <div className={styles.optionsContainer}>
                            {[
                                'Delivery Pacing',
                                'Anxiety Management',
                                'Verbal Clarity',
                                'Strategic Pausing',
                                'Audience Engagement',
                                'Content Structure',
                                'Voice Modulation'
                            ].map(skill => (
                                <button
                                    key={skill}
                                    className={`${styles.optionCard} ${answers.skillToImprove.includes(skill) ? styles.selected : ''}`}
                                    onClick={() => {
                                        const updatedSkills = answers.skillToImprove.includes(skill)
                                            ? answers.skillToImprove.filter(s => s !== skill)
                                            : [...answers.skillToImprove, skill];
                                        setAnswers({ ...answers, skillToImprove: updatedSkills });
                                    }}
                                >
                                    {skill}
                                </button>
                            ))}
                        </div>
                    </div>
                );
            case 6:
                return (
                    <div className={styles.stepSection}>
                        <h2>Assessment Summary</h2>
                        <div className={styles.summaryContainer}>
                            <div className={styles.summaryCard}>
                                <h3>Gender</h3>
                                <p>{answers.gender}</p>
                            </div>
                            <div className={styles.summaryCard}>
                                <h3>Presentation Type</h3>
                                <p>{answers.presentationEnvironment}</p>
                            </div>
                            <div className={styles.summaryCard}>
                                <h3>Confidence Level (Without Practice)</h3>
                                <p>{answers.confidenceWithoutPractice}</p>
                            </div>
                            <div className={styles.summaryCard}>
                                <h3>Confidence Level (With Practice)</h3>
                                <p>{answers.confidenceWithPractice}</p>
                            </div>
                            <div className={styles.summaryCard}>
                                <h3>Focus Areas</h3>
                                <p>{answers.skillToImprove.join(', ')}</p>
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div className={styles.shellContainer}>
                <div className={styles.contentWrapper}>
                    <div className={styles.card}>
                        <div className={styles.loadingSpinner}>Loading...</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.shellContainer}>
            <div className={styles.contentWrapper}>
                <div className={styles.card}>
                    <div className={styles.progressContainer}>
                        <div className={styles.progressBar}>
                            <div
                                className={styles.progressFill}
                                style={{ width: `${(step / 5) * 100}%` }}
                            />
                        </div>
                        <span className={styles.progressText}>Step {step} of 5</span>
                    </div>

                    {error && <div className={styles.errorAlert}>{error}</div>}

                    {renderStep()}

                    <div className={styles.navigationControls}>
                        {step > 1 && (
                            <button
                                className={styles.secondaryButton}
                                onClick={() => setStep(step - 1)}
                            >
                                Previous
                            </button>
                        )}
                        {step < 5 ? (
                            <button
                                className={styles.primaryButton}
                                onClick={() => setStep(step + 1)}
                                disabled={!isStepValid()}
                            >
                                Continue
                            </button>
                        ) : (
                            <button
                                className={styles.primaryButton}
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Saving Changes...' : isEditing ? 'Save Changes' : 'Complete Setup'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Assessment;